import { useLocation, useNavigate } from 'react-router-dom';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import NavigationItem from 'components/NavigationItem';
import AccountIcon from 'components/icons/AccountIcon';
import DataIcon from 'components/icons/DataIcon';
import HelpIcon from 'components/icons/HelpIcon';
import HomeIcon from 'components/icons/HomeIcon';
import ReportsIcon from 'components/icons/ReportsIcon';
import WrenchIcon from 'components/icons/WrenchIcon';

import useFeatureFlags from 'hooks/useFeatureFlags';
import useGlobalStore from 'hooks/useGlobalStore';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { HELP_PAGE, TEST_ID } from './constants';
import type { FunctionComponent, Props, UserResponse } from './types';

import styles from './styles.module.scss';

const Navigation: FunctionComponent<Props> = ({ className = '', isOpen, testId = TEST_ID }) => {
    const { ACCOUNT, DATA, HELP, HOME, MAINTENANCE, REPORTS } = useTranslation();
    const { dataPage, homePage, maintenancePage, reportsPage, userDetailsPage } = usePageRoutes();

    const { getValue } = useGlobalStore({ namespace: 'useCurrentUser' });
    const currentUser = getValue('currentUser') as UserResponse | undefined;

    const { MAINTENANCE_PAGE_FEATURE_FLAG } = useFeatureFlags();

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const accountRoute = currentUser?.id ? userDetailsPage(currentUser.id) : undefined;

    const navigationItems = [
        { Icon: HomeIcon, label: HOME, route: homePage },
        { Icon: DataIcon, label: DATA, route: dataPage },
        { Icon: AccountIcon, label: ACCOUNT, route: accountRoute },
        { Icon: ReportsIcon, label: REPORTS, route: reportsPage },
        ...(MAINTENANCE_PAGE_FEATURE_FLAG
            ? [{ Icon: WrenchIcon, label: MAINTENANCE, route: maintenancePage }]
            : []),
        { Icon: HelpIcon, label: HELP, route: HELP_PAGE }
    ];

    const isMatchingRoute = (route?: string) =>
        !!route && (pathname === route || pathname.startsWith(route + '/'));

    const activeRoute = navigationItems
        .map(({ route }) => route)
        .filter(isMatchingRoute)
        .sort(
            (firstRoute, secondRoute) => (secondRoute?.length ?? 0) - (firstRoute?.length ?? 0)
        )[0];

    const handleNavigation = (route?: string) => {
        if (!route) {
            return;
        }

        if (route.startsWith('http')) {
            window.open(route, '_blank', 'noopener,noreferrer');
        } else {
            navigate(route);
        }
    };

    return (
        <nav className={className} data-testid={testId}>
            {navigationItems.map(({ Icon, label, route }) => {
                const isActive = !!route && route === activeRoute;

                const activeIconStyles = isActive ? styles.active : '';

                return (
                    <VerticalContainer key={label}>
                        <NavigationItem
                            icon={<Icon className={`${styles.icon} ${activeIconStyles}`} />}
                            isActive={isActive}
                            isOpen={isOpen}
                            label={label}
                            onClick={() => handleNavigation(route)}
                        />
                    </VerticalContainer>
                );
            })}
        </nav>
    );
};

export default Navigation;
